import React, { useState, useEffect, useCallback, useRef } from 'react';
import { infrastructureService, procurementService } from '../services/api';
import { lerSecao } from '../design';
import FluxosView from './FluxosView';
import PortaisMatriz from './licitacoes/portais/PortaisMatriz';
import { trocarParametroNaUrl } from '../config/rotas';
import ComprasNetSessionHistory from './ComprasNetSessionHistory';

interface InfraStatus {
  timestamp: string;
  comprasnet_session: {
    status: 'ok' | 'fail' | 'expired' | 'unknown';
    last_check: string | null;
    last_failure: string | null;
    failure_count_24h: number;
    final_url?: string | null;
    latency_ms?: number;
    live?: {
      logged_in?: boolean | null;
      final_url?: string | null;
      latency_ms?: number | null;
      ok?: boolean;
      error?: string | null;
      locked?: boolean;
      lock?: any;
    };
  };
  /** Perna do JWT, separada da do cookie. null = nunca sondado. */
  comprasnet_jwt?: {
    valid: boolean | null;
    exp_in_sec: number | null;
    checked_at: string | null;
    /** sonda com mais de 30min — não afirmar nada a partir dela */
    stale: boolean;
  } | null;
  comprasnet_paused: { paused: boolean; paused_until: string | null; reason: string | null };
  manual_login_lock: { locked: boolean; locked_by?: string; locked_at?: string; reason?: string };
  proxy_tunnel: {
    ok: boolean;
    exit_ip: string | null;
    latency_ms: number | null;
    expected_ip: string;
    proxy: string | null;
    error: string | null;
  };
  proposta_extension?: {
    alive: boolean;
    queue_size: number | null;
    results_size: number | null;
    last_poll_at: number | null;
    last_poll_ago_ms: number | null;
    error?: string | null;
  };
  pncp_egress?: {
    mode: 'mac' | 'residential' | 'vpn' | 'direct' | null;
    residential_available?: boolean;
    mac_available?: boolean;
    egress?: string | null;
    requests?: number;
    ok?: number;
    errors?: number;
    bytes_in?: number;
    last_exit_ip?: string | null;
    test_ms?: number | null;
    test_error?: string | null;
    last_error?: string | null;
    /** Desde quando requests/ok/errors valem (zeram no restart do playwright). */
    counters_since?: string | null;
    cooldown_active?: boolean;
    error?: string;
  };
  captcha_balances?: {
    twocaptcha: { configured: boolean; balance: number | null; error: string | null };
    capsolver: { configured: boolean; balance: number | null; error: string | null };
    brightdata?: { configured: boolean; balance: number | null; pending: number | null; error: string | null };
  };
}

const fmt = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return iso;
  }
};

// Identifica o host por trás do IP de saída (pra deixar claro por onde sai).
const ipHost = (ip?: string | null): string => {
  if (!ip) return '';
  if (ip.startsWith('187.49.84.')) return ' · Mac mini (Cascavel, residencial)';
  if (ip === '56.127.0.82') return ' · financeiro (EC2)';
  if (ip.startsWith('15.228.')) return ' · licitacao-1 (EC2 direto)';
  return '';
};

const StatusDot = ({ color }: { color: 'green' | 'yellow' | 'red' | 'gray' }) => {
  const map = {
    green: 'bg-slate-400 shadow-slate-400/30', // H1 (L6, 24/10/2026): OK é o normal, neutro; âmbar e vermelho seguem
    yellow: 'bg-amber-500 shadow-amber-500/50',
    red: 'bg-rose-500 shadow-rose-500/50',
    gray: 'bg-slate-500 shadow-slate-500/50',
  };
  return <span className={`inline-block w-3 h-3 rounded-full shadow-lg ${map[color]}`} />;
};

const Card = ({
  title,
  color,
  children,
  rightHeader,
}: {
  title: string;
  color: 'green' | 'yellow' | 'red' | 'gray';
  children: React.ReactNode;
  rightHeader?: React.ReactNode;
}) => (
  // `min-w-0`: item de grid também nasce com `min-width: auto` e se recusa a
  // encolher abaixo do conteúdo. Sem isso, um valor comprido dentro do card
  // alarga o card e empurra/cobre o vizinho — que foi o sintoma relatado.
  <div className="bg-central-cartao border border-central-borda rounded-central p-5 shadow-lg min-w-0 overflow-hidden">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <StatusDot color={color} />
        <h3 className="text-base font-semibold text-slate-100">{title}</h3>
      </div>
      {rightHeader}
    </div>
    <div className="space-y-2 text-sm">{children}</div>
  </div>
);

/**
 * Linha rótulo/valor do card.
 *
 * ⛔ O valor VAZAVA PRA FORA DO CARD e cobria o card vizinho. Causa: item de
 * flex tem `min-width: auto`, então ele se recusa a encolher abaixo da largura
 * do próprio conteúdo — e uma string longa sem espaço (a URL do
 * acesso-nao-autorizado, no "Última falha registrada") simplesmente transborda.
 * `min-w-0` devolve o direito de encolher e `break-words` quebra a linha.
 *
 * `block` é para os valores que são texto corrido longo (mensagem de erro, URL
 * de redirect): espremer isso na metade direita de uma linha fica ilegível em
 * qualquer largura. Nesse modo o rótulo fica em cima e o valor ocupa a largura
 * toda, num bloco próprio.
 */
const Row = ({
  label,
  value,
  mono,
  block,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  block?: boolean;
}) =>
  block ? (
    <div className="space-y-1">
      <div className="text-slate-400">{label}</div>
      <div className="bg-central-elevado border border-central-borda rounded px-2 py-1.5 text-slate-300 font-mono text-[11px] leading-snug break-all">
        {value}
      </div>
    </div>
  ) : (
    <div className="flex justify-between items-start gap-4">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span
        className={`text-slate-200 text-right min-w-0 break-words ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value}
      </span>
    </div>
  );


interface Props {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

interface ClaudeUsage {
  total: { calls: number; tokensIn: number; tokensOut: number; costUsd: number };
  byDay: Record<string, { calls: number; costUsd: number }>;
  byFeature: Record<string, { calls: number; costUsd: number }>;
}

interface EditalAiUsage {
  total: { calls: number; tokensIn: number; tokensOut: number; costUsd: number };
  byDay: Record<string, { calls: number; tokensIn: number; tokensOut: number; costUsd: number }>;
  byFeature: Record<string, { calls: number; costUsd: number }>;
  last100: Array<{
    id: number;
    feature: string;
    model: string;
    tokensInput: number;
    tokensOutput: number;
    cacheCreationTokens: number;
    cacheReadTokens: number;
    costUsd: number;
    itemId: number | null;
    durationMs: number | null;
    success: boolean;
    createdAt: string;
  }>;
}

/**
 * Duração legível e "há X". Estavam declarados DENTRO do card do PNCP, então
 * os outros cards não alcançavam — e são exatamente eles que precisam mostrar
 * a janela de medição junto do número.
 */
const fmtDur = (ms: number | null) => {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const hh = Math.floor(m / 60);
  return `${hh}h${m % 60}min`;
};

const fmtTimeAgo = (iso: string | null) => {
  if (!iso) return '—';
  return `há ${fmtDur(Date.now() - new Date(iso).getTime())}`;
};

/** Nomes das features gravadas em claude_api_usage, pro card não mostrar snake_case. */
const FEATURE_LABELS: Record<string, string> = {
  edital_analysis: 'análise de edital',
  proposal_generation: 'geração de proposta',
};

interface AlertaLicitacaoUsage {
  total: number;
  bySourceAggregator: Record<string, number>;
  byPortal: Record<string, number>;
  /** @deprecated max(updatedAt) de qualquer item do AL — anda por scrape e
   *  mudança de status, NÃO é sync. Use lastCronRun/lastNewItemAt. */
  lastSyncAt: string | null;
  /** Última licitação NOVA ingerida (max createdAt) — diz se a coleta trouxe algo. */
  lastNewItemAt?: string | null;
  /** Última rodada registrada em al_sync_logs. Fim de semana não gera linha. */
  lastCronRun?: {
    syncDate: string;
    source: string;
    itemsReturned: number;
    keywords: number;
    at: string;
  } | null;
  keywords?: string[];
  skippedMultiWord?: string[];
}

export default function ComprasNetInfraTab({ showToast }: Props) {
  const [tab, setTab] = useState<'portais' | 'ao-vivo' | 'sessao' | 'custos' | 'fluxos'>(() => (new URLSearchParams(window.location.search).get('portal') ? 'portais' : 'ao-vivo'));
  const [status, setStatus] = useState<InfraStatus | null>(null);
  const [usage, setUsage] = useState<ClaudeUsage | null>(null);
  const [editalUsage, setEditalUsage] = useState<EditalAiUsage | null>(null);
  const [alertaUsage, setAlertaUsage] = useState<AlertaLicitacaoUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  // PNCP API health (probe a cada 5min no backend; aqui só lê o estado).
  const [pncpHealth, setPncpHealth] = useState<Awaited<ReturnType<typeof procurementService.pncpHealth>> | null>(null);
  // Os painéis que NÃO atualizaram na última leitura (08/10/2026). Antes, cada um caía calado em null: na primeira
  // carga o painel simplesmente não aparecia, e numa recarga que falhava ele mostrava a leitura velha como atual.
  const [paineisQueFaltaram, setPaineisQueFaltaram] = useState<{ partes: string[]; motivo: string } | null>(null);
  const [probingPncp, setProbingPncp] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchStatus = useCallback(async () => {
    setRefreshing(true);
    try {
      const [data, usageData, editalUsageData, alertaData, healthData] = await Promise.all([
        infrastructureService.getStatus(),
        lerSecao(infrastructureService.getClaudeUsage()),
        lerSecao(procurementService.aiAnalyzerUsage()),
        lerSecao(procurementService.alertaLicitacaoUsage()),
        lerSecao(procurementService.pncpHealth()),
      ]);
      setStatus(data);
      if (usageData.valor) setUsage(usageData.valor);
      if (editalUsageData.valor) setEditalUsage(editalUsageData.valor);
      if (alertaData.valor) setAlertaUsage(alertaData.valor);
      if (healthData.valor) setPncpHealth(healthData.valor);
      const falhas: Array<[string, string | null]> = [
        ['uso do Claude no captcha', usageData.erro], ['uso da IA nos editais', editalUsageData.erro],
        ['uso do AlertaLicitação', alertaData.erro], ['saúde do PNCP', healthData.erro],
      ];
      const partes = falhas.filter(([, e]) => e).map(([n]) => n);
      setPaineisQueFaltaram(partes.length ? { partes, motivo: falhas.find(([, e]) => e)![1] as string } : null);
    } catch (err: any) {
      console.error('infrastructure status falhou', err);
      showToast('Falha ao buscar status: ' + (err?.message || 'erro'), 'error');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [showToast]);

  const handleSetEgressMode = useCallback(async (mode: 'mac' | 'residential' | 'vpn' | 'direct') => {
    setSwitchingMode(true);
    try {
      const r = await infrastructureService.setPncpEgressMode(mode);
      if (r?.ok) {
        const modeLabel: Record<string, string> = { mac: 'Mac mini (Cascavel)', residential: 'Residencial rotativo (Bright Data)', vpn: 'VPN financeiro', direct: 'Direto (EC2)' };
        showToast(`Modo de saída PNCP: ${modeLabel[r.mode] || r.mode}`, 'success');
        await fetchStatus();
      } else {
        showToast(r?.error || 'Não foi possível trocar o modo', 'error');
      }
    } catch (err: any) {
      showToast('Erro ao trocar modo: ' + (err?.response?.data?.message || err?.message), 'error');
    } finally {
      setSwitchingMode(false);
    }
  }, [showToast, fetchStatus]);

  const probePncpNow = useCallback(async () => {
    setProbingPncp(true);
    try {
      const r = await procurementService.pncpHealthProbe();
      setPncpHealth({ ...(pncpHealth || {} as any), ...r });
      showToast(r.ok ? `✓ PNCP OK (${r.latencyMs}ms)` : `✗ PNCP fora: ${r.error}`, r.ok ? 'success' : 'error');
      await fetchStatus();
    } catch (err: any) {
      showToast('Erro ao verificar PNCP: ' + (err?.message || 'erro'), 'error');
    } finally {
      setProbingPncp(false);
    }
  }, [showToast, fetchStatus, pncpHealth]);

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, 30000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  const handleAction = async (label: string, fn: () => Promise<any>) => {
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      await fn();
      showToast(label + ' OK', 'success');
      await fetchStatus();
    } catch (err: any) {
      showToast(label + ' falhou: ' + (err?.message || 'erro'), 'error');
    } finally {
      setActionInFlight(false);
    }
  };

  const onCreateLock = () =>
    handleAction('Lock criado', () =>
      infrastructureService.createManualLoginLock('admin', 'login_manual_vnc'),
    );
  const onDeleteLock = () =>
    handleAction('Lock removido', () => infrastructureService.deleteManualLoginLock());
  const onPause6h = () =>
    handleAction('Cron pausado 6h', () => infrastructureService.pauseComprasnet(6, 'manual'));
  const onPause24h = () =>
    handleAction('Cron pausado 24h', () => infrastructureService.pauseComprasnet(24, 'manual'));
  const onUnpause = () =>
    handleAction('Cron despausado', () => infrastructureService.unpauseComprasnet());
  const onReset = () =>
    handleAction('Estado resetado', () => infrastructureService.resetComprasnet());

  if (loading) {
    return (
      <div className="p-6 text-slate-400">
        <p>Carregando status...</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="p-6 text-slate-400">
        <p>Sem dados de status.</p>
        <button
          onClick={fetchStatus}
          className="mt-3 px-3 py-1.5 rounded bg-primary hover:bg-primary-strong text-white text-sm"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const session = status.comprasnet_session;
  const lockActive = status.manual_login_lock?.locked;
  const paused = status.comprasnet_paused?.paused;

  /**
   * O cookie SSO e o JWT Bearer são pernas SEPARADAS e morrem em separado — e o
   * card só falava da primeira. Em 13/09/2026 o SSO ficou morto uma hora
   * enquanto o JWT era renovado por HTTP: a tela disse "logado" o tempo todo e
   * mandou o operador embora. Com as duas à vista, o estado meia-boca tem nome:
   * robô opera por API, mas tudo que navega está quebrado até alguém logar.
   */
  /**
   * Rótulo da janela do PNCP. Dizer "24h" sobre 12 sondas de uma hora é o mesmo
   * vício do card de sessão: número que parece histórico e é volátil. Com menos
   * de 4h de amostra (48 sondas a cada 5min) o rótulo passa a dizer o que
   * realmente mediu.
   */
  const pncpProbes = pncpHealth?.probes24h ?? 0;
  const pncpDesde = pncpHealth?.primeiraSondaAt ? new Date(pncpHealth.primeiraSondaAt).getTime() : 0;
  const pncpJanelaMs = pncpDesde ? Date.now() - pncpDesde : 0;
  const janelaPncpLabel = !pncpProbes
    ? '—'
    : pncpJanelaMs >= 4 * 3600_000
      ? '24h'
      : `${fmtDur(pncpJanelaMs)} (${pncpProbes} sonda${pncpProbes > 1 ? 's' : ''})`;

  const jwt = status.comprasnet_jwt;
  const jwtValido = jwt && !jwt.stale ? jwt.valid === true : null;
  const soApi = session.status !== 'ok' && jwtValido === true;

  // Cores derivadas (usa status persistido pelo cron heartbeat)
  // Amarelo pro estado meia-boca (SSO morto, JWT vivo): é degradado, não é
  // queda total, e usar o mesmo vermelho de "tudo fora" apagaria a diferença
  // — que é justamente o que o operador precisa enxergar pra saber se corre ou
  // se só precisa logar.
  const sessionColor: 'green' | 'yellow' | 'red' | 'gray' = lockActive
    ? 'yellow'
    : session.status === 'ok'
    ? 'green'
    : soApi
    ? 'yellow'
    : session.status === 'fail' || session.status === 'expired'
    ? 'red'
    : 'gray';

  const proxyColor: 'green' | 'yellow' | 'red' = !status.proxy_tunnel.ok
    ? 'red'
    : status.proxy_tunnel.exit_ip === status.proxy_tunnel.expected_ip
    ? 'green'
    : 'yellow';

  const pauseColor: 'green' | 'red' = paused ? 'red' : 'green';
  const lockColor: 'green' | 'yellow' = lockActive ? 'yellow' : 'green';

  return (
    <div className="p-6 space-y-6 max-w-[1700px]">
      {paineisQueFaltaram && (
        <div role="alert" className="px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200" data-paineis="nao-atualizaram">
          Não consegui atualizar: {paineisQueFaltaram.partes.join(', ')} ({paineisQueFaltaram.motivo}). Esses painéis mostram
          a última leitura que deu certo, ou não aparecem se nunca leram.
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Automação Licitações</h2>
          <p className="text-slate-400 text-sm mt-1">
            Infra das automações de licitação — ComprasNet, PCP, BLL, AlertaLicitação.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-500">
            Atualizado: {fmt(status.timestamp)}
          </span>
          <button
            onClick={() => handleAction('Heartbeat disparado', () => infrastructureService.forceHeartbeat())}
            disabled={refreshing || actionInFlight}
            className="px-3 py-1.5 rounded bg-primary hover:bg-primary-strong text-white disabled:opacity-50"
            title="Heartbeat rápido — valida se sessão tá viva (~6s)"
          >
            Validar agora
          </button>
          <button
            onClick={() => handleAction('Relogin disparado', () => infrastructureService.forceReloginNew())}
            disabled={refreshing || actionInFlight}
            className="px-3 py-1.5 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-50"
            title="Força auto-login completo (~95s). Use quando sessão expirou."
          >
            🔑 Recuperar sessão (auto-login)
          </button>
          <button
            onClick={fetchStatus}
            disabled={refreshing}
            className="px-3 py-1.5 rounded bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-100 disabled:opacity-50"
          >
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-1 border-b border-central-borda">
        {([
          // A matriz de portais (15/10/2026, aprovada pelo Heitor): a primeira aba. O padrão segue "Ao vivo" (é o que quem
          // opera vigia); com "?portal=" na URL, abre direto na página do portal.
          ['portais', 'Portais'],
          ['ao-vivo', 'Ao vivo'],
          ['sessao', 'Sessão & Uptime'],
          ['custos', 'Custos'],
          ['fluxos', 'Fluxos'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-white text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Aba SESSÃO & UPTIME — histórico/uptime da sessão ComprasNet */}
      {tab === 'sessao' && <ComprasNetSessionHistory showToast={showToast} />}

      {tab === 'portais' && <PortaisMatriz />}

      {/* Aba FLUXOS — documentação visual dos processos (movida do menu raiz 05/07/26) */}
      {tab === 'fluxos' && <FluxosView aoAbrirPortal={(p) => { trocarParametroNaUrl('portal', p); setTab('portais'); }} />}

      {/* Banners de aviso (só na aba Ao vivo) */}
      {tab === 'ao-vivo' && paused && (
        <div className="bg-rose-900/40 border border-rose-700 text-rose-100 px-4 py-3 rounded-lg">
          <strong>Cron ComprasNet PAUSADO</strong> até {fmt(status.comprasnet_paused.paused_until)} ({status.comprasnet_paused.reason}). Despause manualmente quando sessão estiver OK.
        </div>
      )}
      {tab === 'ao-vivo' && lockActive && (
        <div className="bg-amber-900/40 border border-amber-700 text-amber-100 px-4 py-3 rounded-lg">
          <strong>Login manual em andamento</strong> por {status.manual_login_lock.locked_by} desde {fmt(status.manual_login_lock.locked_at || null)}. Healthcheck e cron pausados pra não interferir.
        </div>
      )}

      {(tab === 'ao-vivo' || tab === 'custos') && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {/* Cards operacionais (aba Ao vivo) */}
        {tab === 'ao-vivo' && (<>
        <Card
          title="Sessão ComprasNet (SSO gov.br)"
          color={sessionColor}
          rightHeader={
            <span className="text-xs uppercase tracking-wider text-slate-400">
              {soApi
                ? 'só API'
                : session.status === 'ok' ? 'logado' : session.status === 'expired' ? 'expirado' : 'desconhecido'}
            </span>
          }
        >
          {soApi && (
            <div className="mb-2 text-[11px] leading-snug text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5">
              <strong>SSO expirado, JWT válido.</strong> O robô continua operando pela API
              (cadastro, leitura de compra), mas tudo que NAVEGA — fallback de clique do
              envio, scrape por DOM, sala de disputa — falha até alguém logar no VNC.
            </div>
          )}
          <Row label="Cookie SSO (gov.br)" value={session.status === 'ok' ? '✓ logado' : session.status === 'expired' ? '✗ expirado' : '—'} />
          <Row
            label="JWT Bearer (cnetmobile)"
            value={
              jwtValido === true
                ? `✓ válido${jwt?.exp_in_sec != null ? ` (expira em ${Math.max(0, Math.round(jwt.exp_in_sec / 60))}min)` : ''}`
                : jwtValido === false
                ? '✗ vencido'
                : '— sonda velha (>30min)'
            }
          />
          <Row label="Último check (cron heartbeat)" value={fmt(session.last_check)} />
          <Row label="Última falha registrada (histórico)" value={session.last_failure || '—'} block />
          <Row label="Falhas 24h" value={String(session.failure_count_24h)} />
          <Row label="Cron heartbeat" value="*/3min — 24/7" />
          <Row label="Recovery automático" value="heartbeat faz F5 → warm-JWT → force-relogin inline" />
        </Card>

        <Card title="VPN / Proxy SOCKS5" color={proxyColor}>
          <Row label="Proxy" value={status.proxy_tunnel.proxy || '—'} mono />
          <Row label="Exit IP" value={status.proxy_tunnel.exit_ip ? `${status.proxy_tunnel.exit_ip}${ipHost(status.proxy_tunnel.exit_ip)}` : '—'} mono />
          <Row label="Expected" value={status.proxy_tunnel.expected_ip} mono />
          <Row
            label="IP match"
            value={
              status.proxy_tunnel.exit_ip === status.proxy_tunnel.expected_ip
                ? '✓ esperado'
                : status.proxy_tunnel.exit_ip
                ? '⚠️ diferente'
                : '—'
            }
          />
          <Row label="Latency" value={status.proxy_tunnel.latency_ms ? `${status.proxy_tunnel.latency_ms} ms` : '—'} />
          {status.proxy_tunnel.error && <Row label="Erro" value={status.proxy_tunnel.error} mono />}
        </Card>

        {(() => {
          const eg = status.pncp_egress;
          const fmtBytes = (b?: number) => {
            if (!b) return '0 B';
            if (b < 1024) return `${b} B`;
            if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
            if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
            return `${(b / 1073741824).toFixed(2)} GB`;
          };
          const pncpColor: 'green' | 'yellow' | 'red' | 'gray' =
            !eg || eg.error ? 'gray'
            : eg.test_error ? 'red'
            : eg.cooldown_active ? 'yellow'
            : eg.test_ms != null ? 'green' // teste de saída passou = saudável (qualquer modo)
            : 'yellow';
          return (
            <Card title="Navegação PNCP (egress)" color={pncpColor}>
              {!eg || eg.error ? (
                <Row label="Status" value={eg?.error || 'indisponível'} />
              ) : (
                <>
                  <div className="flex items-center justify-between py-1.5 border-b border-central-borda">
                    <span className="text-sm text-slate-400">Modo</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleSetEgressMode('mac')}
                        disabled={switchingMode || eg.mode === 'mac' || !eg.mac_available}
                        title="Mac mini Cascavel (IP residencial) — padrão"
                        className={`text-xs px-2 py-1 rounded border transition-colors ${eg.mode === 'mac' ? 'bg-central-elevado border-central-borda text-white font-semibold' : 'border-central-borda text-slate-300 hover:bg-central-elevado disabled:opacity-40'}`}
                      >
                        🏠 Mac mini
                      </button>
                      <button
                        onClick={() => handleSetEgressMode('vpn')}
                        disabled={switchingMode || eg.mode === 'vpn'}
                        title="VPN SOCKS5 → financeiro (EC2)"
                        className={`text-xs px-2 py-1 rounded border transition-colors ${eg.mode === 'vpn' ? 'bg-central-elevado border-central-borda text-white font-semibold' : 'border-central-borda text-slate-300 hover:bg-central-elevado disabled:opacity-50'}`}
                      >
                        🔒 VPN
                      </button>
                      <button
                        onClick={() => handleSetEgressMode('residential')}
                        disabled={switchingMode || eg.mode === 'residential' || !eg.residential_available}
                        title={!eg.residential_available ? 'Residencial rotativo (Bright Data) — opcional, não configurado' : 'Usar proxy residencial rotativo (Bright Data)'}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${eg.mode === 'residential' ? 'bg-central-elevado border-central-borda text-white font-semibold' : 'border-central-borda text-slate-300 hover:bg-central-elevado disabled:opacity-40'}`}
                      >
                        🌐 BRD
                      </button>
                    </div>
                  </div>
                  <Row label="Modo ativo" value={{ mac: '🏠 Mac mini (Cascavel)', vpn: '🔒 VPN financeiro', residential: '🌐 Residencial rotativo (BRD)', direct: '➡️ Direto (EC2)' }[eg.mode || ''] || eg.mode || '—'} />
                  <Row label="Egress" value={eg.egress || '—'} mono />
                  <Row label="Exit IP (último teste)" value={eg.last_exit_ip ? `${eg.last_exit_ip}${ipHost(eg.last_exit_ip)}` : '—'} mono />
                  <Row label="Teste ao vivo" value={eg.test_error ? `⚠️ ${eg.test_error}` : eg.test_ms != null ? `✓ ${eg.test_ms} ms` : '—'} />
                  {/* Contadores in-memory do playwright: zeram no restart. A
                      janela vai junto, senão "10 requests, 2 erros" se lê como
                      taxa de erro de 20% quando é o que passou desde o deploy. */}
                  <Row
                    label={eg.counters_since ? `Requests (desde ${fmtTimeAgo(eg.counters_since)})` : 'Requests'}
                    value={`${eg.requests ?? 0} (✓ ${eg.ok ?? 0} · ✗ ${eg.errors ?? 0})`}
                  />
                  <Row label="Tráfego" value={eg.mode === 'residential' ? `${fmtBytes(eg.bytes_in)} / 1 GB plano` : fmtBytes(eg.bytes_in)} />
                  {eg.cooldown_active && <Row label="Cooldown" value="⚠️ ativo (WAF bloqueou — aguardando esfriar)" />}
                  {eg.last_error && <Row label="Último erro" value={eg.last_error} block />}
                  {eg.mode === 'vpn' && (
                    <Row label="Nota" value="IP único — limitado pelo WAF em lote. Ative proxy residencial p/ throughput." />
                  )}
                </>
              )}
            </Card>
          );
        })()}

        {(() => {
          const h = pncpHealth;
          const color: 'green' | 'yellow' | 'red' | 'gray' = !h
            ? 'gray'
            : h.up
              ? (h.consecutiveFailures > 0 ? 'yellow' : 'green')
              : 'red';
          return (
            <Card title="PNCP API" color={color}>
              <Row
                label="Status"
                value={
                  !h ? 'verificando…'
                  : h.up
                    ? (h.consecutiveFailures > 0 ? `⚠️ instável (${h.consecutiveFailures} falha recente)` : '✓ no ar')
                    : `✗ fora${h.downSinceMs ? ` há ${fmtDur(h.downSinceMs)}` : ''}`
                }
              />
              <Row label="Última verificação OK" value={fmtTimeAgo(h?.lastOkAt ?? null)} />
              <Row label="Última checagem" value={fmtTimeAgo(h?.lastCheckAt ?? null)} />
              <Row label="Latência (última)" value={h?.lastLatencyMs != null ? `${h.lastLatencyMs}ms` : '—'} />
              {/* A janela vai JUNTO do número. Antes o card dizia "24h" sobre um
                  histórico in-memory que zerava a cada deploy: em 13/09/2026,
                  com cinco deploys, "Downtime 24h: 0min" era verdade por
                  vacuidade. Agora o rótulo conta quantas sondas sustentam a
                  média, e avisa quando a amostra é curta. */}
              <Row
                label={`Latência média ${janelaPncpLabel}`}
                value={h?.avgLatencyMs != null ? `${h.avgLatencyMs}ms` : '— sem sonda ainda'}
              />
              <Row
                label={`Downtime ${janelaPncpLabel}`}
                value={
                  !h?.probes24h
                    ? '— sem sonda ainda'
                    : h.downtime24hMs
                      ? fmtDur(h.downtime24hMs)
                      : '0min'
                }
              />
              {h?.probesWindowSource === 'memoria' && !!h?.probes24h && (
                <Row
                  label="⚠️ janela"
                  value="histórico do banco indisponível — medindo só desde o restart"
                />
              )}
              {h?.lastError && <Row label="Último erro" value={h.lastError} block />}
              <div className="pt-2 flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500">Probe automático cron 5min. Crons pesados pulam se fora.</span>
                <button
                  onClick={probePncpNow}
                  disabled={probingPncp}
                  className="text-xs px-2 py-1 rounded border border-central-borda text-slate-300 hover:bg-central-elevado disabled:opacity-50"
                >
                  {probingPncp ? '⏳ verificando…' : '🔄 Verificar agora'}
                </button>
              </div>
            </Card>
          );
        })()}

        <Card
          title="Extension Firefox (ComprasNet)"
          color={status.proposta_extension?.alive ? 'green' : 'red'}
          rightHeader={
            <span className="text-xs uppercase tracking-wider text-slate-400">
              {status.proposta_extension?.alive ? 'ativa' : 'offline'}
            </span>
          }
        >
          <Row label="Extension viva" value={status.proposta_extension?.alive ? '✓ polling' : '✗ sem poll > 30s'} />
          <Row label="Último poll" value={status.proposta_extension?.last_poll_ago_ms !== null && status.proposta_extension?.last_poll_ago_ms !== undefined ? `há ${Math.round(status.proposta_extension.last_poll_ago_ms / 1000)}s` : '—'} />
          <Row label="Jobs na fila" value={status.proposta_extension?.queue_size !== null && status.proposta_extension?.queue_size !== undefined ? String(status.proposta_extension.queue_size) : '—'} />
          <Row label="Resultados em cache" value={status.proposta_extension?.results_size !== null && status.proposta_extension?.results_size !== undefined ? String(status.proposta_extension.results_size) : '—'} />
          {status.proposta_extension?.error && <Row label="Erro" value={status.proposta_extension.error} mono />}
        </Card>

        {/* Card Chromium-Xvfb removido em 27/05/2026 — browser aposentado, tudo
            migrado pra Firefox+extension. /health/cdp também removido do backend. */}

        <Card title="Cron + Lock state" color={paused ? 'red' : lockActive ? 'yellow' : 'green'}>
          <Row label="Cron pausado" value={paused ? '✗ SIM' : '✓ não'} />
          {paused && <Row label="Até" value={fmt(status.comprasnet_paused.paused_until)} />}
          {paused && <Row label="Motivo" value={status.comprasnet_paused.reason || '—'} />}
          <Row label="Manual login lock" value={lockActive ? '✗ ATIVO' : '✓ livre'} />
          {lockActive && <Row label="Por" value={status.manual_login_lock.locked_by || '—'} />}
          {lockActive && <Row label="Desde" value={fmt(status.manual_login_lock.locked_at || null)} />}
        </Card>
        </>)}

        {/* Cards de custo (aba Custos) */}
        {tab === 'custos' && (<>
        {usage && (
          <Card title="Custo Claude API (resolução de captcha)" color="green">
            <Row label="Total acumulado" value={`US$ ${usage.total.costUsd.toFixed(4)}`} />
            <Row label="Total chamadas" value={String(usage.total.calls)} />
            <Row label="Tokens entrada" value={usage.total.tokensIn.toLocaleString('pt-BR')} />
            <Row label="Tokens saída" value={usage.total.tokensOut.toLocaleString('pt-BR')} />
            <Row label="Hoje" value={`US$ ${(usage.byDay[new Date().toISOString().substring(0, 10)]?.costUsd || 0).toFixed(4)} (${usage.byDay[new Date().toISOString().substring(0, 10)]?.calls || 0} chamadas)`} />
            <Row label="Custo médio/captcha" value={usage.total.calls > 0 ? `US$ ${(usage.total.costUsd / usage.total.calls).toFixed(4)}` : '—'} />
            <Row label="Modo" value="Opus 4.7 + self-critique 2-pass" />
          </Card>
        )}

        {editalUsage && (() => {
          const today = new Date().toISOString().substring(0, 10);
          const todayStats = editalUsage.byDay[today] || { calls: 0, tokensIn: 0, tokensOut: 0, costUsd: 0 };
          const last = editalUsage.last100[0] || null;
          return (
            <Card title="Custo Claude API (editais + propostas)" color="green">
              <Row label="Total acumulado" value={`US$ ${editalUsage.total.costUsd.toFixed(4)}`} />
              {/* O total SEMPRE somou geração de proposta junto (getUsageStats
                  agrega a tabela inteira, sem filtro de feature), mas o título
                  dizia só "análise de editais" e o byFeature — que já vinha no
                  payload — era descartado. Em 13/09/2026, 94% do valor exibido
                  era proposal_generation debaixo do rótulo de edital. */}
              {Object.entries(editalUsage.byFeature || {})
                .sort((a, b) => b[1].costUsd - a[1].costUsd)
                .map(([feature, v]) => (
                  <Row
                    key={feature}
                    label={`  ↳ ${FEATURE_LABELS[feature] || feature}`}
                    value={`US$ ${v.costUsd.toFixed(4)} (${v.calls})`}
                  />
                ))}
              <Row label="Total chamadas" value={String(editalUsage.total.calls)} />
              <Row label="Tokens entrada" value={editalUsage.total.tokensIn.toLocaleString('pt-BR')} />
              <Row label="Tokens saída" value={editalUsage.total.tokensOut.toLocaleString('pt-BR')} />
              <Row label="Hoje" value={`US$ ${todayStats.costUsd.toFixed(4)} (${todayStats.calls} chamada(s))`} />
              <Row label="Custo médio/chamada" value={editalUsage.total.calls > 0 ? `US$ ${(editalUsage.total.costUsd / editalUsage.total.calls).toFixed(4)}` : '—'} />
              <Row label="Última chamada" value={last ? fmt(last.createdAt) : '—'} />
              {/* Modelo LIDO do último registro, não escrito à mão: o rótulo
                  fixo dizia "Sonnet 4.5" enquanto o serviço já rodava
                  claude-sonnet-5, e ninguém tem como perceber isso na tela. */}
              <Row label="Modelo" value={last?.model ? `${last.model} + prompt caching` : '—'} />
            </Card>
          );
        })()}

        {status.captcha_balances && (() => {
          const cb = status.captcha_balances;
          const fmtBal = (p: { configured: boolean; balance: number | null; error: string | null }) =>
            !p.configured ? 'não configurado'
              : p.error ? `erro: ${p.error}`
              : p.balance != null ? `US$ ${p.balance.toFixed(3)}`
              : '—';
          const bd = cb.brightdata;
          const low = [cb.twocaptcha, cb.capsolver, ...(bd ? [bd] : [])].some(
            (p: any) => p.configured && (p.error || (p.balance != null && p.balance < 1)),
          );
          return (
            <Card title="Saldo solvers / proxy" color={low ? 'yellow' : 'green'}>
              <Row label="2captcha" value={fmtBal(cb.twocaptcha)} mono />
              <Row label="capsolver" value={fmtBal(cb.capsolver)} mono />
              <Row label="Bright Data" value={bd ? fmtBal(bd) : 'não configurado'} mono />
              <Row label="Uso" value={<span className="text-xs">2captcha → PCP edital (reCAPTCHA Enterprise ~US$ 0,003) · Bright Data → BLL captcha</span>} />
              <Row label="Alerta" value={<span className="text-xs">amarelo se saldo &lt; US$ 1 ou erro de API</span>} />
            </Card>
          );
        })()}
        </>)}

        {tab === 'ao-vivo' && alertaUsage && (() => {
          const topPortais = Object.entries(alertaUsage.byPortal)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);
          const totalAL = alertaUsage.bySourceAggregator['alertalicitacao'] || 0;
          return (
            <Card
              title="AlertaLicitação (sync agregador)"
              color={alertaUsage.lastSyncAt ? 'green' : 'gray'}
              rightHeader={
                <span className="text-xs uppercase tracking-wider text-slate-400">
                  {totalAL > 0 ? `${totalAL} licit.` : 'sem dados'}
                </span>
              }
            >
              <Row label="Licitações via AL" value={totalAL.toLocaleString('pt-BR')} />
              <Row label="Total no banco" value={alertaUsage.total.toLocaleString('pt-BR')} />
              <Row label="Outras fontes (PNCP direto/manual)" value={(alertaUsage.bySourceAggregator['pncp_direct_or_manual'] || 0).toLocaleString('pt-BR')} />
              {/* "Último sync" saiu: ele vinha de max(updatedAt) de qualquer
                  item do AL, e esse timestamp anda por scrape, mudança de
                  status, re-enrich. Em 13/09/2026 (domingo) o card exibia
                  "11:20" num dia em que o cron PULOU às 06:04 — lia-se como
                  coleta feita. Os dois campos abaixo respondem o que o
                  operador precisa: quando a coleta rodou, e se ela trouxe algo. */}
              <Row
                label="Última coleta (cron)"
                value={
                  alertaUsage.lastCronRun
                    ? `${alertaUsage.lastCronRun.syncDate} — ${alertaUsage.lastCronRun.keywords} keyword(s), ` +
                      `${alertaUsage.lastCronRun.itemsReturned} item(ns)` +
                      (alertaUsage.lastCronRun.source && alertaUsage.lastCronRun.source !== 'cron-daily'
                        ? ` (${alertaUsage.lastCronRun.source})`
                        : '')
                    : '— nenhuma rodada registrada'
                }
              />
              <Row
                label="Último item NOVO"
                value={alertaUsage.lastNewItemAt ? fmt(alertaUsage.lastNewItemAt) : '—'}
              />
              <div className="text-[10px] text-slate-500 pt-1 leading-snug">
                Sem linha de coleta em sábado/domingo é esperado: o cron pula o fim de semana
                de propósito (órgão não publica edital novo, e a rodada custa ~R$&nbsp;3-4).
              </div>
              {topPortais.length > 0 && (
                <Row
                  label="Top portais"
                  value={
                    <span className="text-xs">
                      {topPortais.map(([portal, count]) => `${portal}: ${count}`).join(' · ')}
                    </span>
                  }
                />
              )}
              <Row label="Cron diário" value="06:00-06:09 BRT" />
              <Row label="Keywords" value={<span className="text-xs">{alertaUsage.keywords?.length ? `${alertaUsage.keywords.join(', ')} (${alertaUsage.keywords.length})` : '—'}</span>} />
              {alertaUsage.skippedMultiWord && alertaUsage.skippedMultiWord.length > 0 && (
                <Row label="Ignoradas (multi-palavra)" value={<span className="text-xs text-amber-400">{alertaUsage.skippedMultiWord.join(', ')}</span>} />
              )}
            </Card>
          );
        })()}
      </div>
      )}

      {/* Ações administrativas (aba Ao vivo) */}
      {tab === 'ao-vivo' && (
      <div className="bg-central-cartao border border-central-borda rounded-central p-5">
        <h3 className="text-base font-semibold text-slate-100 mb-3">Ações administrativas</h3>
        <p className="text-slate-400 text-sm mb-4">
          Use o lock antes de logar manualmente via VNC pra evitar que cron interfira. Pause cron por horas em emergência. Reset limpa contadores + remove pause (use após login manual confirmar sessão OK).
        </p>
        <div className="flex flex-wrap gap-2">
          {!lockActive ? (
            <button
              onClick={onCreateLock}
              disabled={actionInFlight}
              className="px-3 py-2 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm font-medium disabled:opacity-50"
            >
              🔒 Criar lock (estou logando via VNC)
            </button>
          ) : (
            <button
              onClick={onDeleteLock}
              disabled={actionInFlight}
              className="px-3 py-2 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm font-medium disabled:opacity-50"
            >
              🔓 Liberar lock (terminei de logar)
            </button>
          )}

          {!paused ? (
            <>
              <button
                onClick={onPause6h}
                disabled={actionInFlight}
                className="px-3 py-2 rounded bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-100 text-sm disabled:opacity-50"
              >
                ⏸️ Pausar cron 6h
              </button>
              <button
                onClick={onPause24h}
                disabled={actionInFlight}
                className="px-3 py-2 rounded bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-100 text-sm disabled:opacity-50"
              >
                ⏸️ Pausar cron 24h
              </button>
            </>
          ) : (
            <button
              onClick={onUnpause}
              disabled={actionInFlight}
              className="px-3 py-2 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm font-medium disabled:opacity-50"
            >
              ▶️ Despausar cron
            </button>
          )}

          <button
            onClick={onReset}
            disabled={actionInFlight}
            className="px-3 py-2 rounded border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-sm disabled:opacity-50"
            title="Limpa failure count + remove pause + status volta pra unknown. Use depois que logar manual + healthcheck virar verde."
          >
            🔄 Reset state
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
